import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import IconButton from '@material-ui/core/IconButton'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import AddAPhotoIcon from 'components/material-symbol/icons/AddAPhoto'
import CloseIcon from 'components/material-symbol/icons/Close'
import DescriptionIcon from 'components/material-symbol/icons/Description'
import { uploadMedia } from 'lib/cloudinary'
import { CLOUDINARY_CLOUDNAME, CLOUDINARY_UPLOAD_PRESET } from 'env'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import { MAX_MEDIA, MAX_OFFERINGS, MESSAGE_MAX_LENGTH } from '../constants'
import { formatShortDate } from '../format'
import useStyles from '../styles'
import SectionTitle from 'components/section-title'

const UPLOAD_OPTIONS = {
  cloudName: CLOUDINARY_CLOUDNAME,
  upload_preset: CLOUDINARY_UPLOAD_PRESET,
  sources: ['local'],
  multiple: false,
  folder: 'coach-campaigns',
  showPoweredBy: false,
}

const IMAGE_OPTIONS = {
  ...UPLOAD_OPTIONS,
  resourceType: 'image',
  clientAllowedFormats: ['png', 'jpeg', 'jpg', 'gif', 'webp'],
  maxFileSize: 10000000,
}

const FILE_OPTIONS = {
  ...UPLOAD_OPTIONS,
  resourceType: 'auto',
  clientAllowedFormats: ['pdf'],
  maxFileSize: 20000000,
}

const fileTitle = info =>
  [info.original_filename, info.format].filter(Boolean).join('.')

const offeringShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  startDateTime: PropTypes.string,
})

const OfferingGroup = ({
  title,
  offeringType,
  options,
  selected,
  onToggle,
}) => {
  const classes = useStyles()
  const unavailable = selected
    .filter(
      offering =>
        offering.offeringType === offeringType &&
        !options.some(option => option.id === offering.id),
    )
    .map(offering => ({ id: offering.id, title: offering.title }))
  const rows = [...options, ...unavailable]
  if (rows.length === 0) return null
  const atLimit = selected.length >= MAX_OFFERINGS

  return (
    <Box>
      <Typography className={classes.fieldGroupLabel}>{title}</Typography>
      <Box className={classes.previewList}>
        {rows.map(option => {
          const isUnavailable = unavailable.includes(option)
          const checked = selected.some(
            offering =>
              offering.offeringType === offeringType &&
              offering.id === option.id,
          )
          return (
            <Box key={option.id} className={classes.previewRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={checked}
                    disabled={!checked && atLimit}
                    onChange={() => onToggle(offeringType, option)}
                  />
                }
                label={
                  <Typography className={classes.previewName}>
                    {option.title}
                    {option.startDateTime
                      ? ` · ${formatShortDate(option.startDateTime)}`
                      : ''}
                    {isUnavailable ? ' · No longer available' : ''}
                  </Typography>
                }
              />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

OfferingGroup.propTypes = {
  title: PropTypes.string.isRequired,
  offeringType: PropTypes.oneOf(['LESSON_TYPE', 'GROUP_EVENT']).isRequired,
  options: PropTypes.arrayOf(offeringShape).isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
}

const MediaTile = ({ item, onRemove }) => {
  const classes = useStyles()

  return (
    <Box className={classes.mediaTile}>
      {item.kind === 'IMAGE' ? (
        <img
          src={item.url}
          alt={item.title || ''}
          className={classes.mediaImage}
        />
      ) : (
        <>
          <DescriptionIcon />
          <Typography className={classes.mediaTitle}>
            {item.title || 'Attachment'}
          </Typography>
        </>
      )}
      <IconButton
        size="small"
        className={classes.mediaRemove}
        onClick={onRemove}
        aria-label={`Remove ${item.title || 'attachment'}`}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

MediaTile.propTypes = {
  item: PropTypes.shape({
    kind: PropTypes.oneOf(['IMAGE', 'FILE']).isRequired,
    url: PropTypes.string.isRequired,
    title: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
}

export default function ContentStep({
  campaign,
  offeringOptions,
  bodyError,
  onChange,
}) {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const showError = useSnackbarError(enqueueSnackbar)
  const { body, offerings, media } = campaign
  const mediaRemaining = MAX_MEDIA - media.length

  const toggleOffering = (offeringType, option) => {
    const exists = offerings.some(
      offering =>
        offering.offeringType === offeringType && offering.id === option.id,
    )
    onChange({
      offerings: exists
        ? offerings.filter(
          offering =>
            !(
              offering.offeringType === offeringType &&
              offering.id === option.id
            ),
        )
        : [...offerings, { offeringType, id: option.id, title: option.title }],
    })
  }

  const addMedia = items =>
    onChange({ media: [...media, ...items].slice(0, MAX_MEDIA) })

  const upload = async kind => {
    try {
      const info = await uploadMedia(
        kind === 'IMAGE' ? IMAGE_OPTIONS : FILE_OPTIONS,
      )
      if (!info) return
      addMedia([
        kind === 'IMAGE'
          ? { kind, url: info.secure_url, title: fileTitle(info) }
          : {
            kind,
            url: info.secure_url,
            title: fileTitle(info),
            mimeType: 'application/pdf',
          },
      ])
    } catch (e) {
      showError('That upload didn’t work. Try again.')
    }
  }

  return (
    <>
      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <SectionTitle>Message</SectionTitle>
          <Typography className={classes.cardSubtitle}>
            Students get this in chat from you. Anyone who hasn’t opened the app
            gets it by email too.
          </Typography>
        </Box>
        <Box className={classes.cardBody}>
          <TextField
            id="campaign-body"
            variant="outlined"
            label="Message"
            placeholder="Spring clinics are open! I saved spots for my students first."
            value={body}
            onChange={event => onChange({ body: event.target.value })}
            inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
            error={bodyError}
            helperText={
              bodyError
                ? 'Write a message'
                : `${body.length} / ${MESSAGE_MAX_LENGTH}`
            }
            multiline
            minRows={5}
            fullWidth
          />
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <SectionTitle>Offerings</SectionTitle>
          <Typography className={classes.cardSubtitle}>
            Add up to {MAX_OFFERINGS}. Each one shows as a card students can
            book from, and we track bookings that come from it.
          </Typography>
        </Box>
        <Box className={classes.cardBody}>
          {offeringOptions.lessonTypes.length === 0 &&
          offeringOptions.groupEvents.length === 0 ? (
            <Typography className={classes.helperText}>
              You don’t have any active booking types or upcoming group events
              to add.
            </Typography>
          ) : null}
          <OfferingGroup
            title="Booking types"
            offeringType="LESSON_TYPE"
            options={offeringOptions.lessonTypes}
            selected={offerings}
            onToggle={toggleOffering}
          />
          <OfferingGroup
            title="Upcoming group events"
            offeringType="GROUP_EVENT"
            options={offeringOptions.groupEvents}
            selected={offerings}
            onToggle={toggleOffering}
          />
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <SectionTitle>Photos and guides</SectionTitle>
          <Typography className={classes.cardSubtitle}>
            Add promo images or PDF practice guides. Up to {MAX_MEDIA}.
          </Typography>
        </Box>
        <Box className={classes.cardBody}>
          {media.length > 0 ? (
            <Box className={classes.mediaGrid}>
              {media.map((item, index) => (
                <MediaTile
                  key={`${item.kind}-${item.url}-${index}`}
                  item={item}
                  onRemove={() =>
                    onChange({ media: media.filter((_, i) => i !== index) })
                  }
                />
              ))}
            </Box>
          ) : null}
          <Box className={classes.mediaActions}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddAPhotoIcon />}
              disabled={mediaRemaining <= 0}
              onClick={() => upload('IMAGE')}
            >
              Add Image
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DescriptionIcon />}
              disabled={mediaRemaining <= 0}
              onClick={() => upload('FILE')}
            >
              Add PDF
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  )
}

ContentStep.propTypes = {
  campaign: PropTypes.shape({
    body: PropTypes.string.isRequired,
    offerings: PropTypes.array.isRequired,
    media: PropTypes.array.isRequired,
  }).isRequired,
  offeringOptions: PropTypes.shape({
    lessonTypes: PropTypes.arrayOf(offeringShape).isRequired,
    groupEvents: PropTypes.arrayOf(offeringShape).isRequired,
  }).isRequired,
  bodyError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
}
